import type { SocialConnection } from '@mg/shared';

const DAY_MS = 24 * 60 * 60 * 1000;

/** A sign-in that ends within this many days is warned about (the card turns amber). */
export const SIGN_IN_WARNING_DAYS = 7;

/**
 * Whole calendar days from today until `iso`, in the viewer's time zone: 0 = today, 1 = tomorrow,
 * negative = already past. Calendar days rather than 24-hour periods, so "Expires today" and
 * "Expires tomorrow" are true as the viewer reads them. Null when there is no (valid) date.
 */
export function daysUntil(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null;
  const end = new Date(iso);
  if (Number.isNaN(end.getTime())) return null;
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  // Rounded: a day that a clock change made 23 or 25 hours long is still one day.
  return Math.round((startOfDay(end) - startOfDay(now)) / DAY_MS);
}

/**
 * The days left on a connected account's sign-in when it ends within a week, else null. An account
 * that already needs a Reconnect is not "ending soon": the red state says more, and wins.
 * The Connections card and the Messages banner both ask here, so they cannot disagree.
 */
export function signInEndingSoon(conn: SocialConnection, now?: Date): number | null {
  if (!conn.connected || conn.needsReconnect === true) return null;
  const days = daysUntil(conn.signInExpiresAt, now);
  return days != null && days <= SIGN_IN_WARNING_DAYS ? days : null;
}

/** The card's badge: "Expires in 5 days" / "Expires tomorrow" / "Expires today". */
export function expiresLabel(days: number): string {
  if (days <= 0) return 'Expires today';
  return days === 1 ? 'Expires tomorrow' : `Expires in ${days} days`;
}

/** The day a sign-in ends, in the viewer's locale - "Oct 20, 2026". */
export function formatSignInEnd(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });
}
